import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { toast } from 'react-toastify';
import { debounce } from '@mui/material';
import { tableHelper } from 'common/helpers/table/table-helper';
import {
    MasterLayout,
    MainLayout,
    ToolbarLayout,
    ToolbarLayoutLeft,
    ToolbarLayoutRight,
} from 'common/imports/content-layout';
import { DeleteModal } from 'common/partials/DeleteModal';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { RolesDictionary } from 'common/helpers/dictionaries/roles-dictionary';
import ProtectedComponent from 'common/partials/ProtectedComponent';
import ActionButton from 'common/partials/ActionButton';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { ProcessModel as Model } from '../model/process.model';
import { ProcessesService as Service } from '../services/processes.service';
import { ProcessesFilterDropdown } from './childs/ProcessesFilterDropdown';

export function Processes() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<Model[]>([]);
    // const [filteredTableData, setFilteredTableData] = useState<Model[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<Model[]>([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [filterStatus, setFilterStatus] = useState('true');
    const [deleteId, setDeleteId] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [queryString, setQueryString] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(100);
    const [sorting, setSorting] = useState<{ orderBy?: string; order?: 'asc' | 'desc' }>({});
    const [departmentFilter, setDepartmentFilter] = useState<number | undefined>();

    const handleShowDeleteModal = (id: number) => {
        setShowDeleteModal(true);
        setDeleteId(id);
    };
    const handleCloseDeleteModal = () => setShowDeleteModal(false);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await Service.getDataList(
                departmentFilter,
                filterStatus,
                queryString,
                currentPage,
                rowsPerPage,
                sorting.orderBy,
                sorting.order
            );

            if (response && !response.hasErrors) {
                setTableData(response.data.items);
                setTotalRows(response.data.totalCount);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setIsLoading(false);
        }
    };

    const updateQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
            setFilterStatus('true');
        } else {
            setFilterStatus('');
        }
        setQueryString(e.target.value);
    };

    const debouncedChange = debounce(updateQuery, 500);

    const columns: TableColumn<Model>[] = [
        {
            name: `${t('table.id')}`,
            selector: (row) => row.id || '',
            sortable: true,
            sortField: 'Id',
            width: '60px'
        },
        {
            name: `${t('table.name')}`,
            selector: (row) => row.name || '',
            sortable: true,
            sortField: 'Name'
        },
        {
            name: `${t('table.description')}`,
            selector: (row) => row.description || '',
            sortable: true,
            sortField: 'Description'
        },
        {
            name: `${t('table.departmentName')}`,
            selector: (row) =>
                row.departments && row.departments.length > 0
                    ? row.departments.map(department => department.name).join(', ')
                    : '-',
            sortable: true,
            sortField: 'DepartmentName'
        },
        {
            name: `${t('table.status')}`,
            cell: (row) =>
                row.isActive ? (
                    <span className="badge badge-light-primary">{t('generalMessages.active')}</span>
                ) : (
                    <span className="badge badge-light-danger">{t('generalMessages.inactive')}</span>
                ),
            width: '150px'
        },
        {
            name: `${t('table.actions')}`,
            cell: (row) => (
                <div className="d-grid d-flex gap-2">
                    <ActionButton type='edit' onClick={() => navigate(`/processes/process/${row.id}`)} roles={[RolesDictionary.Process.Processes_Update]} />
                    <ActionButton type='deactivate' onClick={() => handleShowDeleteModal(row.id!)} roles={[RolesDictionary.Process.Processes_Update]} disabled={!row.isActive} />
                </div>
            ),
            width: '100px'
        },
    ];

    const handleRowSelected = useCallback((state: any) => {
        setSelectedRows(state.selectedRows);
    }, []);

    const deleteDataById = async () => {
        try {
            setIsLoading(true);
            const result = await Service.deleteDataById(deleteId);
            if (result && !result.hasErrors) {
                toast.success(`${t('crud.deactivate.successMessage')}`);
            } else {
                toast.error(`${t('crud.deactivate.errorMessage')}`);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setToggleCleared(!toggleCleared);
            handleCloseDeleteModal();
            fetchData();
            setIsLoading(false);
        }
    };

    const contextActions = useMemo(() => {
        const deleteSelectedRows = () => {
            setShowDeleteModal(true);
            selectedRows.forEach((row) => {
                setDeleteId(row.id!);
            });
        }

        return (
            <button type="button" className="btn btn-sm btn-danger" onClick={deleteSelectedRows}>
                {t('crud.deactivate.buttonSave')}
            </button>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, toggleCleared]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, rowsPerPage, sorting, filterStatus, departmentFilter, queryString]);

    const handleListSort = (selectedColumn: TableColumn<Model>, sortDirection: 'asc' | 'desc') => {
        setTableData([]);
        setSorting({
            orderBy: selectedColumn.sortField,
            order: sortDirection
        });
    };

    const handleFilterChange = async (departmentId?: number, status?: string) => {
        setDepartmentFilter(departmentId);
        setFilterStatus(status || '');
        setCurrentPage(1);
        await fetchData();
    };

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight>
                    <div className="d-flex align-items-center gap-2">
                        <ProcessesFilterDropdown fetchProcesses={handleFilterChange} statusFilter={filterStatus} />
                        <ProtectedComponent action='hide' role={[RolesDictionary.Process.Processes_Update]}>
                            <Link to="/processes/process/add" className="btn btn-sm btn-primary fw-bolder">
                                {t('addProcess')}
                            </Link>
                        </ProtectedComponent>
                    </div>
                </ToolbarLayoutRight>
            </ToolbarLayout>
            <MainLayout>
                <div className="card card-flush">
                    <div className="card-body">
                        <DataTable
                            title={tableHelper.TableHeader(debouncedChange)}
                            persistTableHead={true}
                            columns={columns}
                            data={tableData}
                            noDataComponent={tableHelper.NoDataComponent()}
                            selectableRows
                            selectableRowsHighlight
                            dense
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationComponentOptions={tableHelper.PaginationComponentOptions()}
                            paginationPerPage={rowsPerPage}
                            paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                            onChangePage={(page) => setCurrentPage(page)}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                setRowsPerPage(newPerPage);
                                setCurrentPage(page);
                            }}
                            highlightOnHover
                            progressPending={isLoading}
                            progressComponent={tableHelper.Loader()}
                            contextActions={contextActions}
                            contextMessage={tableHelper.ContextMessage()}
                            onSelectedRowsChange={handleRowSelected}
                            clearSelectedRows={toggleCleared}
                            selectableRowsVisibleOnly={true}
                            customStyles={itLabTableStyle}
                            onSort={handleListSort}
                            sortServer
                        />
                    </div>
                </div>

                <DeleteModal
                    type='deactivate'
                    handleDelete={deleteDataById}
                    onClose={handleCloseDeleteModal}
                    open={showDeleteModal}
                />
            </MainLayout>
        </MasterLayout>
    );
}
